import {
	Button,
	Inline,
	Text,
	Divider,
	Stack,
	Box,
	Icons,
} from "@sembark-travel/ui-base"
import { useXHR } from "@sembark-travel/xhr"
import {
	IPermission,
	IRole,
	SelectRoles,
	getUniqueRevocablePermissions,
} from "./../Roles"
import {
	Form,
	withServerErrors,
	SubmissionError,
	SelectField,
	validateFormValues,
	FieldArray,
	CheckboxInputField,
	arrayMutators,
	useForm,
	useFieldValue,
} from "@sembark-travel/ui-form"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { Alert } from "@sembark-travel/ui-base"
import * as Validator from "yup"
import { IUser, XHR } from "./store"
import { useMemo, useState } from "react"
import { PERMISSIONS, useHasFeatureFlag } from "../Auth"

interface EditRolesProps {
	user: IUser
	onSuccess: () => void
	onCancel?: () => void
}

export function EditUserRolesForm({
	user,
	onSuccess,
	onCancel,
}: EditRolesProps) {
	const xhr = useXHR()
	const hasMultiCurrency = useHasFeatureFlag("multi_currency")
	const { name, roles = [], revoked_permissions } = user
	const initialRevokablePermissions = useMemo(
		() =>
			getUniqueRevocablePermissions(roles, revoked_permissions).filter(
				(p) =>
					!(
						p.name === PERMISSIONS.MODIFY_QUOTE_EXCHANGE_RATE &&
						!hasMultiCurrency
					)
			),
		[roles, revoked_permissions, hasMultiCurrency]
	)

	const initialValues = {
		roles: roles,
		revokable_permissions: initialRevokablePermissions,
	}

	return (
		<Form<typeof initialValues>
			initialValues={initialValues}
			validate={validateFormValues(
				Validator.object().shape({
					roles: Validator.array().min(
						1,
						"Please select atleast one role for the member"
					),
				})
			)}
			onSubmit={withServerErrors(async (values) => {
				await XHR(xhr).updateRoles(user.id, {
					revoked_permissions: (values?.revokable_permissions || []).reduce<
						string[]
					>((acc, p) => {
						if (!p.is_revokable) {
							acc.push(p.name)
						}
						return acc
					}, []),
					roles: values.roles.map(({ internal_name }) => ({
						name: internal_name,
					})),
				})
				onSuccess()
			})}
			subscription={{ submitting: true }}
			mutators={{ ...arrayMutators }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap={"1"}>
						<Inline gap="3" alignItems="center">
							{user?.profile_image_url ? (
								<Box
									as="img"
									src={user?.profile_image_url}
									alt="Avatar"
									style={{ width: "60px", height: "60px" }}
									borderWidth="1"
									borderColor="default"
									borderRadius="full"
								/>
							) : (
								<Box padding="3" borderRadius="full" backgroundColor="primary">
									<Icons.UserRound size="7" color="primary" />
								</Box>
							)}
							<Text fontSize="lg" fontWeight="semibold" color="default">
								{name}
							</Text>
						</Inline>
						<Divider sm />
						<RevocablePermissionFields hasMultiCurrency={hasMultiCurrency} />
					</Stack>
					<Divider sm />
					<SubmissionError />

					<Inline gap="3" justifyContent="start">
						<Button type="submit" level="primary" disabled={submitting}>
							{submitting ? (
								<Inline gap="2" alignItems="center">
									<Icons.Spinner size="4" className="animate-spin" />
									<Text>Updating...</Text>
								</Inline>
							) : (
								"Assign Role"
							)}
						</Button>
						{onCancel ? (
							<Button onClick={onCancel} level="tertiary" disabled={submitting}>
								Cancel
							</Button>
						) : null}
					</Inline>
				</form>
			)}
		</Form>
	)
}

export function RevocablePermissionFields({
	hasMultiCurrency,
}: {
	hasMultiCurrency?: boolean
}) {
	const form = useForm<{
		roles?: Array<IRole>
		revokable_permissions?: Array<IPermission>
	}>()

	const [isDialogOpen, openDialog, closeDialog] = useDialog()
	const { value: permissions, onChange: changeRevokablePermissions } =
		useFieldValue<Array<IPermission>>("revokable_permissions")

	const [snapshot, setSnapshot] = useState<Array<IPermission>>([])

	function handleOpenDialog() {
		setSnapshot(permissions)
		openDialog()
	}

	function handleCancel() {
		changeRevokablePermissions(snapshot)
		closeDialog()
	}

	function handleSave() {
		closeDialog()
	}

	const grantedPermissions = permissions.filter((p) => p.is_revokable)
	const revokedPermissions = permissions.filter((p) => !p.is_revokable)
	const revokedCount = revokedPermissions.length
	const hasNoRestrictions = permissions.length > 0 && revokedCount === 0

	return (
		<Stack gap="4">
			<SelectField
				select={SelectRoles}
				name="roles"
				fetchOnMount
				multiple
				label="Select Role(s)"
				onChange={(value: IRole[], name) => {
					form.change(name as "roles", value)
					changeRevokablePermissions(
						getUniqueRevocablePermissions(value).filter(
							(p) =>
								!(
									p.name === PERMISSIONS.MODIFY_QUOTE_EXCHANGE_RATE &&
									!hasMultiCurrency
								)
						)
					)
				}}
			/>

			{permissions.length > 0 && (
				<Stack gap="2">
					<Box
						width="full"
						padding="3"
						borderWidth="1"
						borderColor="default"
						borderRadius="lg"
						backgroundColor="subtle"
					>
						<Stack gap="4">
							<Text fontSize="base" fontWeight="semibold" color="default">
								What This User Can Access
							</Text>

							<Stack gap="3">
								{hasNoRestrictions ? (
									<Inline gap="2" alignItems="center">
										<Icons.OkCircleSolid size="4" color="success" />
										<Text fontSize="sm" color="success" fontWeight="semibold">
											No restrictions
										</Text>
									</Inline>
								) : (
									<Stack gap="2">
										<Text fontSize="base" color="default" fontWeight="semibold">
											Restricted to:
										</Text>
										<Inline gap="2" style={{ flexWrap: "wrap" }}>
											{revokedPermissions.map((p) => (
												<Box
													key={p.id}
													paddingX="2"
													paddingY="1"
													borderWidth="1"
													borderColor="danger"
													borderRadius="md"
													backgroundColor="danger"
												>
													<Inline gap="1" alignItems="center">
														<Icons.CancelCircleSolid size="3" color="danger" />
														<Text
															fontSize="sm"
															fontWeight="semibold"
															color="danger"
														>
															{p.title || p.name}
														</Text>
													</Inline>
												</Box>
											))}
										</Inline>
									</Stack>
								)}

								{grantedPermissions.length > 0 && (
									<Stack gap="2">
										<Text fontSize="base" color="default" fontWeight="semibold">
											Allowed to:
										</Text>
										<Inline gap="2" style={{ flexWrap: "wrap" }}>
											{grantedPermissions.map((p) => (
												<Box
													key={p.id}
													paddingX="2"
													paddingY="1"
													borderWidth="1"
													borderColor="success"
													borderRadius="md"
													backgroundColor="success"
												>
													<Inline gap="1" alignItems="center">
														<Icons.OkCircleSolid size="3" color="success" />
														<Text
															fontSize="sm"
															fontWeight="semibold"
															color="success"
														>
															{p.title || p.name}
														</Text>
													</Inline>
												</Box>
											))}
										</Inline>
									</Stack>
								)}
							</Stack>
						</Stack>
					</Box>

					<Box
						onClick={handleOpenDialog}
						cursor="pointer"
						style={{ alignSelf: "flex-start" }}
						marginY="2"
					>
						<Inline gap="1" alignItems="center">
							<Text fontSize="sm" color="accent" fontWeight="semibold">
								Manage Permissions
							</Text>
							<Icons.ChevronDown rotate="270" size="4" color="accent" />
						</Inline>
					</Box>
				</Stack>
			)}

			<Dialog
				open={isDialogOpen}
				title="Revocable Permissions"
				onClose={handleCancel}
			>
				<Dialog.Body>
					<Stack gap="4">
						<Alert
							status="warning"
							title="If the user should NOT have access to the following permission(s), 
								please deselect the checkbox to revoke the access."
						/>

						<Box style={{ maxHeight: "24rem" }} overflowY="auto">
							<Box style={{ maxHeight: "24rem" }} overflowY="auto">
								<FieldArray<IPermission> name="revokable_permissions">
									{({ fields }) => (
										<Stack gap="2">
											{fields.map((name, index) => {
												const item = permissions[index]
												const isRevoked = !item.is_revokable
												const checkboxId = `permission-checkbox-${item.id}`

												return (
													<Box
														key={item.id}
														padding="3"
														borderWidth="1"
														borderColor={isRevoked ? "danger" : "default"}
														borderRadius="md"
														backgroundColor={isRevoked ? "danger" : "success"}
													>
														<Inline gap="3" alignItems="start">
															<CheckboxInputField
																id={checkboxId}
																name={`${name}.is_revokable`}
																type="checkbox"
															/>
															<label htmlFor={checkboxId}>
																<Text
																	fontSize="base"
																	fontWeight="semibold"
																	color="default"
																>
																	{item?.title || item?.name || "Permission"}
																</Text>
															</label>
														</Inline>
														{item.description ? (
															<Box paddingLeft="6">
																<Text fontSize="sm" color="muted">
																	{item.description}
																</Text>
															</Box>
														) : null}
													</Box>
												)
											})}
										</Stack>
									)}
								</FieldArray>
							</Box>
						</Box>
					</Stack>
				</Dialog.Body>
				<Dialog.Footer>
					<Button onClick={handleSave} level="primary">
						Save Changes
					</Button>
					<Button onClick={handleCancel} level="tertiary">
						Cancel
					</Button>
				</Dialog.Footer>
			</Dialog>
		</Stack>
	)
}
